import React, { useEffect } from 'react'
import useSWR from 'swr'
import SpektrixFetcher from '@lib/spektrix-fetcher'
import BasketUpdateEvent from '@lib/basket-update-event'
import { ShoppingBagIcon } from '@heroicons/react/24/outline'
import { ShoppingBagIcon as ShoppingBagIconSolid } from '@heroicons/react/24/solid'

const BasketStatus = ({ className = '', iconOnly = false }) => {
  const basketUrl = '/basket'
  const { data: basketContents, error, mutate } = useSWR(basketUrl, SpektrixFetcher, {
    // Don't retry on error as it might be a network issue
    shouldRetryOnError: false,
    // Revalidate on focus to keep basket fresh
    revalidateOnFocus: true
  })
  

  // Listen for basket update events
  useEffect(() => {
    const handleBasketUpdate = () => {
      // Re-fetch basket contents when basket is updated
      mutate()
    }

    window.addEventListener(BasketUpdateEvent, handleBasketUpdate)

    // Cleanup
    return () => {
      window.removeEventListener(BasketUpdateEvent, handleBasketUpdate)
    }
  }, [mutate])

  // Calculate total number of items
  // Exclude donations that are linked to memberships to avoid double counting
  const numItems = basketContents
    ? (basketContents.merchandiseItems?.length || 0) +
      (basketContents.tickets?.length || 0) +
      (basketContents.donations?.filter(donation => {
        // Exclude donations that are linked to a membership
        return !basketContents.membershipSubscriptions?.some(membership => 
          membership.relatedDonationId === donation.id
        )
      }).length || 0) +
      (basketContents.membershipSubscriptions?.length || 0) +
      (basketContents.giftVouchers?.length || 0)
    : 0

  const hasItems = numItems > 0

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount / 100) // Spektrix returns amounts in pence
  }

  // Handle loading state - show empty basket icon
  if (!basketContents && !error) {
    if (iconOnly) {
      return (
        <div className={`relative inline-block ${className}`}>
          <ShoppingBagIcon className="w-5 h-5 text-gray-600 dark:text-gray-400 opacity-50" />
        </div>
      )
    }
    return null
  }

  // Handle error state - show empty basket that links to local basket
  if (error) {
    if (iconOnly) {
      return (
        <a
          href="/basket"
          className={`relative inline-block p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors ${className}`}
          title="View basket"
        >
          <ShoppingBagIcon className="w-5 h-5" />
        </a>
      )
    }
    return null
  }

  // Don't render anything if basket is empty and iconOnly is false
  if (!hasItems && !iconOnly) {
    return null
  }

  // Basket URL - use local route
  const basketHref = "/basket"

  // Icon only mode
  if (iconOnly) {
    return (
      <a
        href={basketHref}
        className={`relative inline-block p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors ${className}`}
        title={hasItems ? `${numItems} items in basket` : 'Basket is empty'}
      >
        {hasItems ? (
          <>
            <ShoppingBagIconSolid className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
              {numItems}
            </span>
          </>
        ) : (
          <ShoppingBagIcon className="w-5 h-5" />
        )}
      </a>
    )
  }

  // Full display mode
  return (
    <a
      href={basketHref}
      className={`flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors ${className}`}
    >
      {hasItems ? (
        <ShoppingBagIconSolid className="w-5 h-5" />
      ) : (
        <ShoppingBagIcon className="w-5 h-5" />
      )}
      <span className="hidden sm:inline">
        {hasItems ? (
          <>
            {numItems} {numItems === 1 ? 'item' : 'items'} - {formatCurrency(basketContents.total)}
          </>
        ) : (
          'Basket'
        )}
      </span>
      <span className="inline sm:hidden">
        {hasItems && numItems}
      </span>
    </a>
  )
}

export default BasketStatus